import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, Video, FileText, Image, Trash2, Edit2, X, Plus, 
  Search, Check, Dumbbell, Utensils, Trophy, Mic, BookOpen,
  Brain, ChevronDown, Play, Calendar, ArrowUp, ArrowDown, GripVertical, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configuración de pestañas/secciones
const CONTENT_TABS = [
  { id: 'rutinas', label: 'Videos de Rutinas', icon: Dumbbell, color: 'pink', description: 'Videos de entrenamiento por nivel y día (Plan de cada nivel)' },
  { id: 'academia', label: 'Academia', icon: BookOpen, color: 'cyan', description: 'Contenido educativo de la biblioteca (técnicas, métodos)' },
  { id: 'ebooks', label: 'Ebooks', icon: FileText, color: 'blue', description: 'PDFs de entrenamiento y nutrición' },
  { id: 'challenges', label: 'Challenges', icon: Trophy, color: 'amber', description: 'Retos y desafíos' },
  { id: 'recetas', label: 'Recetas', icon: Utensils, color: 'orange', description: 'Videos y ebooks de recetas' },
  { id: 'mindset', label: 'Mindset/Podcasts', icon: Brain, color: 'purple', description: 'Contenido de mindset y podcasts' },
];

// Niveles de programa
const PROGRAM_LEVELS = [
  { id: 'nivel_1', name: 'Nivel 1 - Principiante', days: 4, color: 'bg-blue-500' },
  { id: 'nivel_2', name: 'Nivel 2 - Intermedio', days: 5, color: 'bg-purple-500' },
  { id: 'nivel_3', name: 'Premium', days: 6, color: 'bg-pink-500' },
];

const AdminContentManager = ({ embedded = false }) => {
  const [activeTab, setActiveTab] = useState('rutinas');
  const [activeLevel, setActiveLevel] = useState('nivel_1');
  const [contents, setContents] = useState([]);
  const [routineVideos, setRoutineVideos] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null); // Para editar video individual
  const [editVideoForm, setEditVideoForm] = useState({ title: '', description: '', video_url: '', video_source: 'upload' });
  const [programs, setPrograms] = useState([]);
  
  const fileInputRef = useRef(null);
  
  // Obtener headers con token actualizado
  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Estado del formulario de upload
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'entrenamiento',
    levels: ['nivel_1'], // Múltiples niveles
    day: 1,
    access_options: ['free'],
    price: '',
    discount_code: '', // Código de descuento
    discount_percentage: '', // Porcentaje de descuento
    file: null,
    files: [], // Múltiples archivos para rutinas
    coverImage: null, // Imagen de portada para ebooks
    // Campos adicionales para Blog
    author: '',
    blog_category: '',
    // Campos adicionales para Podcasts
    guest: '',
    duration: '',
    // Campos adicionales para Challenges
    start_date: '',
    end_date: '',
    max_participants: '',
    telegram_link: '',
    challenge_benefits: '',
    // Campos adicionales para Academia
    academia_category: 'entrenamiento', // entrenamiento o nutricion
    // Opciones de fuente de video
    video_source: 'upload', // 'upload', 'youtube', 'drive'
    external_url: '', // URL de YouTube o Google Drive
    program_ids: [], // Programas Vinculados (opcional)
  });
  
  const coverInputRef = useRef(null);

  useEffect(() => {
    fetchContents();
  }, [activeTab, activeLevel]);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/programs`, { headers: getHeaders() });
      const fetched = res.data || [];
      // Añadir opción sintética "Acceso Gratuito" al principio.
      setPrograms([
        { program_id: '__FREE__', name: '🌍 Acceso Gratuito (Abierto)' },
        ...fetched,
      ]);
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  // Traduce la selección de "Programas Vinculados" en el payload real:
  // - __FREE__          -> visible para todos (level=free, access=free, active=true, program_ids=[])
  // - reales            -> restringido a esos programas (active=true, program_ids=[...])
  // - vacío             -> borrador inactivo (active=false, program_ids=[])
  const resolveAccessPayload = (selectedProgramIds = []) => {
    const arr = selectedProgramIds || [];
    const hasFree = arr.includes('__FREE__');
    const realIds = arr.filter(p => p && p !== '__FREE__');
    if (hasFree) {
      return {
        program_ids: [],
        levels: ['free'],
        access_options: ['free'],
        active: true,
      };
    }
    if (realIds.length > 0) {
      return {
        program_ids: realIds,
        levels: ['premium'],
        access_options: ['premium_only'],
        active: true,
      };
    }
    return {
      program_ids: [],
      levels: ['free'],
      access_options: ['free'],
      active: false, // borrador inactivo
    };
  };

  const toggleProgramForForm = (formState, setFormState, programId) => {
    const current = formState.program_ids || [];
    // programsTouched: marca que se cambió la selección (en edición, solo así se modifica el acceso al guardar)
    if (current.includes(programId)) {
      setFormState({ ...formState, programsTouched: true, program_ids: current.filter(p => p !== programId) });
    } else {
      setFormState({ ...formState, programsTouched: true, program_ids: [...current, programId] });
    }
  };

  const fetchContents = async () => {
    setLoading(true);
    try {
      // Para rutinas, obtener videos de programa
      if (activeTab === 'rutinas') {
        const response = await axios.get(`${BACKEND_URL}/api/program-content/${activeLevel}`, { headers: getHeaders() });
        setRoutineVideos(response.data?.routines || []);
      }
      
      // Obtener contenido general filtrado por categoría
      const categoryMap = {
        rutinas: 'entrenamiento',
        academia: 'academia',
        ebooks: 'ebook',
        challenges: 'challenge',
        blog: 'blog',
        recetas: 'recetas',
        mindset: 'mindset'
      };
      
      let url = `${BACKEND_URL}/api/content`;
      const params = new URLSearchParams();
      params.append('category', categoryMap[activeTab] || 'entrenamiento');
      if (activeTab === 'rutinas') {
        params.append('level', activeLevel);
      }
      url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: getHeaders() });
      setContents(response.data);
    } catch (error) {
      console.error('Error fetching contents:', error);
      setRoutineVideos([]);
      setContents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ 
        ...uploadForm, 
        file, 
        title: uploadForm.title || file.name.split('.')[0] 
      });
    }
  };

  // Manejar múltiples archivos para rutinas
  const handleMultipleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setUploadForm({
        ...uploadForm,
        files: files,
        file: files[0], // También guardar el primer archivo en file para compatibilidad
        title: uploadForm.title || files[0].name.split('.')[0]
      });
    }
  };

  const openUploadModal = (day = null) => {
    setSelectedDay(day);
    setUploadForm({
      title: '', // Vacío para que el admin escriba el nombre del ejercicio
      description: '',
      category: activeTab === 'rutinas' ? 'entrenamiento' : 
                activeTab === 'academia' ? 'academia' :
                activeTab === 'ebooks' ? 'ebook' :
                activeTab === 'challenges' ? 'challenge' :
                activeTab === 'blog' ? 'blog' :
                activeTab === 'recetas' ? 'recetas' : 'mindset',
      levels: activeTab === 'rutinas' ? [activeLevel] : ['free'],
      day: day || 1,
      access_options: activeTab === 'rutinas' ? [activeLevel] : ['free'],
      price: '',
      discount_code: '',
      discount_percentage: '',
      file: null,
      files: [],
      coverImage: null,
      academia_category: 'entrenamiento',
      video_source: 'upload',
      external_url: '',
      author: '',
      blog_category: '',
      guest: '',
      duration: '',
      start_date: '',
      end_date: '',
      max_participants: '',
      telegram_link: '',
      challenge_benefits: '',
    });
    setShowUploadModal(true);
  };

  // Manejar selección de imagen de portada
  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, coverImage: file });
    }
  };

  const handleUpload = async () => {
    // Validaciones según el tipo de fuente
    const isExternalVideo = uploadForm.video_source === 'youtube' || uploadForm.video_source === 'drive';
    const isEbookWithDrive = activeTab === 'ebooks' && uploadForm.video_source === 'drive';
    
    if (!uploadForm.title) {
      toast.error('Agrega un título');
      return;
    }
    
    // Para videos externos o ebooks con Drive, validar URL
    if (isExternalVideo) {
      if (!uploadForm.external_url) {
        toast.error(isEbookWithDrive ? 'Agrega la URL del ebook en Drive' : 'Agrega la URL del video');
        return;
      }
      // Validar formato de URL - más flexible
      if (uploadForm.video_source === 'youtube') {
        const ytRegex = /(youtube\.com|youtu\.be)/i;
        if (!ytRegex.test(uploadForm.external_url)) {
          toast.error('URL de YouTube no válida. Debe contener youtube.com o youtu.be');
          return;
        }
      }
      if (uploadForm.video_source === 'drive') {
        const driveRegex = /drive\.google\.com/i;
        if (!driveRegex.test(uploadForm.external_url)) {
          toast.error('URL de Google Drive no válida. Debe contener drive.google.com');
          return;
        }
      }
    } else {
      // Para rutinas con archivos
      if (activeTab === 'rutinas' && selectedDay) {
        if (uploadForm.files.length === 0 && !uploadForm.file) {
          toast.error('Selecciona al menos un video');
          return;
        }
      } else if (!uploadForm.file && activeTab !== 'blog') {
        toast.error('Selecciona un archivo');
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // === CASO 1: Video externo (YouTube o Drive) ===
      if (isExternalVideo) {
        let coverPath = null;
        
        // Subir imagen de portada si existe
        if (uploadForm.coverImage) {
          const coverFormData = new FormData();
          coverFormData.append('image', uploadForm.coverImage);
          const coverResponse = await axios.post(`${BACKEND_URL}/api/content/upload-cover`, coverFormData, {
            headers: { Authorization: getHeaders().Authorization }
          });
          coverPath = coverResponse.data.url;
        }
        
        // Crear contenido con URL externa
        const contentData = {
          title: uploadForm.title,
          description: uploadForm.description,
          category: uploadForm.category,
          levels: uploadForm.levels,
          access_options: uploadForm.access_options,
          price: uploadForm.price ? parseFloat(uploadForm.price) : null,
          discount_code: uploadForm.discount_code,
          discount_percentage: uploadForm.discount_percentage ? parseInt(uploadForm.discount_percentage) : null,
          video_source: uploadForm.video_source,
          external_url: uploadForm.external_url,
          cover_image: coverPath,
          academia_category: uploadForm.academia_category,
          ...resolveAccessPayload(uploadForm.program_ids),
        };
        
        const response = await axios.post(`${BACKEND_URL}/api/content/create-external`, contentData, {
          headers: getHeaders()
        });
        
        // Si es rutina, agregar al programa
        if (activeTab === 'rutinas' && selectedDay) {
          await axios.post(
            `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine`,
            {
              day: selectedDay,
              title: uploadForm.title,
              description: uploadForm.description,
              video_url: uploadForm.external_url,
              video_source: uploadForm.video_source
            },
            { headers: getHeaders() }
          );
        }
        
        toast.success(
          activeTab === 'ebooks' 
            ? '¡Ebook de Drive agregado!' 
            : `¡Video de ${uploadForm.video_source === 'youtube' ? 'YouTube' : 'Drive'} agregado!`
        );
        setUploadProgress(100);
      }
      // === CASO 2: Rutina con archivo local ===
      else if (activeTab === 'rutinas' && selectedDay && (uploadForm.files.length > 0 || uploadForm.file)) {
        const file = uploadForm.files[0] || uploadForm.file;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', uploadForm.title);
        formData.append('description', uploadForm.description);
        formData.append('category', uploadForm.category);
        const access = resolveAccessPayload(uploadForm.program_ids);
        formData.append('levels', access.levels.join(','));
        formData.append('access_options', access.access_options.join(','));
        formData.append('active', access.active ? 'true' : 'false');
        if (access.program_ids.length > 0) formData.append('program_ids', access.program_ids.join(','));
        if (uploadForm.price) formData.append('price', uploadForm.price);
        if (uploadForm.discount_code) formData.append('discount_code', uploadForm.discount_code);
        if (uploadForm.coverImage) formData.append('cover_image', uploadForm.coverImage);
        if (uploadForm.program_ids && uploadForm.program_ids.length > 0) {
          formData.append('program_ids', uploadForm.program_ids.join(','));
        }

        const response = await axios.post(`${BACKEND_URL}/api/content/upload`, formData, {
          headers: { Authorization: getHeaders().Authorization },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });

        await axios.post(
          `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine`,
          {
            day: selectedDay,
            title: uploadForm.title,
            description: uploadForm.description,
            video_url: response.data.file_path
          },
          { headers: getHeaders() }
        );
        
        toast.success(`¡Video "${uploadForm.title}" subido exitosamente!`);
      }
      // === CASO 3: Upload normal de archivo ===
      else {
        const formData = new FormData();
        if (uploadForm.file) formData.append('file', uploadForm.file);
        formData.append('title', uploadForm.title);
        formData.append('description', uploadForm.description);
        formData.append('category', uploadForm.category);
        const accessNormal = resolveAccessPayload(uploadForm.program_ids);
        formData.append('levels', accessNormal.levels.join(','));
        formData.append('access_options', accessNormal.access_options.join(','));
        formData.append('active', accessNormal.active ? 'true' : 'false');
        if (accessNormal.program_ids.length > 0) formData.append('program_ids', accessNormal.program_ids.join(','));
        if (uploadForm.price) formData.append('price', uploadForm.price);
        if (uploadForm.discount_code) formData.append('discount_code', uploadForm.discount_code);
        if (uploadForm.discount_percentage) formData.append('discount_percentage', uploadForm.discount_percentage);
        if (uploadForm.coverImage) formData.append('cover_image', uploadForm.coverImage);
        if (uploadForm.author) formData.append('author', uploadForm.author);
        if (uploadForm.blog_category) formData.append('blog_category', uploadForm.blog_category);
        if (uploadForm.guest) formData.append('guest', uploadForm.guest);
        if (uploadForm.duration) formData.append('duration', uploadForm.duration);
        if (uploadForm.start_date) formData.append('start_date', uploadForm.start_date);
        if (uploadForm.end_date) formData.append('end_date', uploadForm.end_date);
        if (uploadForm.max_participants) formData.append('max_participants', uploadForm.max_participants);
        if (uploadForm.telegram_link) formData.append('telegram_link', uploadForm.telegram_link);
        if (uploadForm.challenge_benefits) formData.append('challenge_benefits', uploadForm.challenge_benefits);
        if (uploadForm.academia_category) formData.append('academia_category', uploadForm.academia_category);
        if (uploadForm.program_ids && uploadForm.program_ids.length > 0) {
          formData.append('program_ids', uploadForm.program_ids.join(','));
        }

        await axios.post(`${BACKEND_URL}/api/content/upload`, formData, {
          headers: { Authorization: getHeaders().Authorization },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        });

        toast.success('¡Contenido subido exitosamente!');
      }

      setShowUploadModal(false);
      setUploadForm({ title: '', description: '', category: 'entrenamiento', levels: ['nivel_1'], day: 1, access_options: ['free'], price: '', discount_code: '', discount_percentage: '', file: null, files: [], coverImage: null, author: '', blog_category: '', guest: '', duration: '', start_date: '', end_date: '', max_participants: '', telegram_link: '', challenge_benefits: '', academia_category: 'entrenamiento', video_source: 'upload', external_url: '' });
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el contenido');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('¿Estás segura de eliminar este contenido?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/content/${contentId}`, { headers: getHeaders() });
      toast.success('Contenido eliminado');
      fetchContents();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const deleteRoutineVideo = async (day, videoIndex = null) => {
    const message = videoIndex !== null 
      ? `¿Eliminar el video ${videoIndex + 1} del día ${day}?`
      : `¿Eliminar todos los videos del día ${day}?`;
    if (!window.confirm(message)) return;

    try {
      const url = videoIndex !== null
        ? `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${day}/${videoIndex}`
        : `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${day}`;
      await axios.delete(url, { headers: getHeaders() });
      toast.success('Video de rutina eliminado');
      fetchContents();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Mover video arriba o abajo
  const moveRoutineVideo = async (day, videoIndex, direction) => {
    const dayRoutine = routineVideos.find(r => r.day === day);
    if (!dayRoutine || !dayRoutine.videos) return;

    const videos = [...dayRoutine.videos];
    const newIndex = direction === 'up' ? videoIndex - 1 : videoIndex + 1;
    
    if (newIndex < 0 || newIndex >= videos.length) return;

    // Intercambiar posiciones
    [videos[videoIndex], videos[newIndex]] = [videos[newIndex], videos[videoIndex]];

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${day}/reorder`,
        { videos },
        { headers: getHeaders() }
      );
      toast.success('Orden actualizado');
      fetchContents();
    } catch (error) {
      toast.error('Error al reordenar');
    }
  };

  // Abrir modal de edición de video
  const openEditVideoModal = (day, videoIndex, video) => {
    setEditingVideo({ day, videoIndex });
    setEditVideoForm({
      title: video.title || '',
      description: video.description || '',
      video_url: video.video_url || '',
      video_source: video.video_source || 'upload'
    });
  };

  // Guardar edición de video
  const saveVideoEdit = async () => {
    if (!editingVideo) return;
    const { day, videoIndex } = editingVideo;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${day}/${videoIndex}`,
        editVideoForm,
        { headers: getHeaders() }
      );
      toast.success('Video actualizado');
      setEditingVideo(null);
      fetchContents();
    } catch (error) {
      toast.error('Error al actualizar video');
    }
  };

  // Mover video a otro día
  const moveVideoToDay = async (fromDay, videoIndex, toDay) => {
    const fromRoutine = routineVideos.find(r => r.day === fromDay);
    if (!fromRoutine || !fromRoutine.videos) return;

    const video = fromRoutine.videos[videoIndex];

    try {
      // Primero agregar al nuevo día
      await axios.post(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${toDay}/add-video`,
        video,
        { headers: getHeaders() }
      );
      
      // Luego eliminar del día original
      await axios.delete(
        `${BACKEND_URL}/api/admin/program-content/${activeLevel}/routine/${fromDay}/${videoIndex}`,
        { headers: getHeaders() }
      );
      
      toast.success(`Video movido al día ${toDay}`);
      fetchContents();
    } catch (error) {
      toast.error('Error al mover video');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const currentTabConfig = CONTENT_TABS.find(t => t.id === activeTab);
  const currentLevel = PROGRAM_LEVELS.find(l => l.id === activeLevel);

  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? 'space-y-6' : 'min-h-screen bg-[#09090b] py-12';

  return (
    <Wrapper className={wrapperClass}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6'}>
        {/* Header */}
        {!embedded && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">Gestión de Contenido</h1>
            <p className="text-zinc-400">Sube y administra todo el contenido de la plataforma</p>
          </div>
        )}

        {/* PESTAÑAS DE SECCIONES */}
        <div className="bg-zinc-900 rounded-2xl p-2 border border-zinc-800 mb-6">
          <div className="flex flex-wrap gap-2">
            {CONTENT_TABS.map((tab) => {
              const Icon = tab.icon;
              const colorClasses = {
                pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
                cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
                blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
                green: 'bg-green-500/20 text-green-400 border-green-500/30',
                orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
                purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
              };
              const activeClasses = {
                pink: 'bg-pink-600 text-white',
                cyan: 'bg-cyan-600 text-white',
                blue: 'bg-blue-600 text-white',
                amber: 'bg-amber-600 text-white',
                green: 'bg-green-600 text-white',
                orange: 'bg-orange-600 text-white',
                purple: 'bg-purple-600 text-white',
              };
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                    activeTab === tab.id 
                      ? activeClasses[tab.color]
                      : `${colorClasses[tab.color]} hover:opacity-80`
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Descripción de la sección actual */}
        <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 text-white rounded-2xl p-6 mb-6 border border-zinc-700">
          <div className="flex items-center gap-3">
            {currentTabConfig && <currentTabConfig.icon className="w-8 h-8" />}
            <div>
              <h2 className="text-xl font-bold">{currentTabConfig?.label}</h2>
              <p className="text-zinc-300">{currentTabConfig?.description}</p>
            </div>
          </div>
        </div>

        {/* SELECTOR DE NIVEL (Solo para Rutinas) */}
        {activeTab === 'rutinas' && (
          <div className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800 mb-6">
            <h3 className="font-bold text-white mb-3">Selecciona el Nivel</h3>
            <div className="flex flex-wrap gap-2">
              {PROGRAM_LEVELS.map((level) => (
                <button
                  key={level.id}
                  onClick={() => setActiveLevel(level.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    activeLevel === level.id
                      ? `${level.color} text-white`
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {level.name} ({level.days} días)
                </button>
              ))}
            </div>
          </div>
        )}

        {/* CONTENIDO POR SECCIÓN */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        ) : (
          <>
            {/* SECCIÓN: VIDEOS DE RUTINAS */}
            {activeTab === 'rutinas' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white">
                    Videos de Rutinas - {currentLevel?.name}
                  </h3>
                </div>

                {/* Grid de días */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: currentLevel?.days || 4 }, (_, idx) => {
                    const day = idx + 1;
                    // Buscar la rutina del día
                    const dayRoutine = routineVideos.find(r => r.day === day);
                    // Obtener videos del array 'videos' o del formato antiguo
                    let dayVideos = [];
                    if (dayRoutine) {
                      if (dayRoutine.videos && dayRoutine.videos.length > 0) {
                        dayVideos = dayRoutine.videos;
                      } else if (dayRoutine.video_url) {
                        // Formato antiguo: convertir a nuevo formato
                        dayVideos = [{ video_url: dayRoutine.video_url, title: dayRoutine.title }];
                      }
                    }
                    const hasVideos = dayVideos.length > 0;
                    
                    return (
                      <div 
                        key={day} 
                        className={`rounded-2xl p-5 border-2 transition-all ${
                          hasVideos 
                            ? 'bg-green-500/10 border-green-500/50' 
                            : 'bg-zinc-900 border-zinc-700 border-dashed'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
                            hasVideos ? 'bg-green-500 text-white' : `${currentLevel?.color} text-white`
                          }`}>
                            {day}
                          </div>
                          <div>
                            <h4 className="font-bold text-white">Día {day}</h4>
                            <p className="text-sm text-zinc-400">
                              {hasVideos ? `${dayVideos.length} video${dayVideos.length > 1 ? 's' : ''}` : 'Sin contenido'}
                            </p>
                          </div>
                        </div>

                        {hasVideos ? (
                          <div className="space-y-3">
                            {/* Lista de videos del día con controles completos */}
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {dayVideos.map((video, vIdx) => (
                                <div key={vIdx} className="bg-zinc-800 rounded-xl p-3 border border-zinc-700 hover:border-green-500/50 transition-all">
                                  <div className="flex items-center gap-2">
                                    {/* Indicador de orden */}
                                    <div className="flex flex-col gap-0.5">
                                      <button
                                        onClick={() => moveRoutineVideo(day, vIdx, 'up')}
                                        disabled={vIdx === 0}
                                        className={`p-0.5 rounded ${vIdx === 0 ? 'text-zinc-400' : 'text-zinc-400 hover:text-green-400 hover:bg-green-500/20'}`}
                                        title="Mover arriba"
                                      >
                                        <ArrowUp className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => moveRoutineVideo(day, vIdx, 'down')}
                                        disabled={vIdx === dayVideos.length - 1}
                                        className={`p-0.5 rounded ${vIdx === dayVideos.length - 1 ? 'text-zinc-400' : 'text-zinc-400 hover:text-green-400 hover:bg-green-500/20'}`}
                                        title="Mover abajo"
                                      >
                                        <ArrowDown className="w-3 h-3" />
                                      </button>
                                    </div>
                                    
                                    {/* Número de orden */}
                                    <span className="w-5 h-5 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                      {vIdx + 1}
                                    </span>
                                    
                                    {/* Info del video */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs text-green-400 font-medium truncate">
                                        {video.title || `Video ${vIdx + 1}`}
                                      </p>
                                      {video.video_source && video.video_source !== 'upload' && (
                                        <span className="text-[10px] text-zinc-500">
                                          {video.video_source === 'youtube' ? '📺 YouTube' : '📁 Drive'}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Botones de acción */}
                                    <div className="flex gap-1 flex-shrink-0">
                                      <a 
                                        href={video.video_url?.startsWith('http') ? video.video_url : `${BACKEND_URL}${video.video_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-green-500/20 text-green-400 p-1.5 rounded-lg hover:bg-green-500/30 transition-colors"
                                        title="Ver video"
                                      >
                                        <Play className="w-3 h-3" />
                                      </a>
                                      <button
                                        onClick={() => openEditVideoModal(day, vIdx, video)}
                                        className="bg-blue-500/20 text-blue-400 p-1.5 rounded-lg hover:bg-blue-500/30 transition-colors"
                                        title="Editar video"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => deleteRoutineVideo(day, vIdx)}
                                        className="bg-red-500/20 text-red-400 p-1.5 rounded-lg hover:bg-red-500/30 transition-colors"
                                        title="Eliminar video"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {/* Selector para mover a otro día */}
                                  <div className="mt-2 pt-2 border-t border-zinc-700">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-zinc-500">Mover a:</span>
                                      <select
                                        className="text-[10px] border border-zinc-700 rounded px-1 py-0.5 bg-zinc-900 text-white"
                                        value=""
                                        onChange={(e) => {
                                          if (e.target.value) {
                                            moveVideoToDay(day, vIdx, parseInt(e.target.value));
                                          }
                                        }}
                                      >
                                        <option value="">Seleccionar día...</option>
                                        {Array.from({ length: currentLevel?.days || 4 }, (_, i) => i + 1)
                                          .filter(d => d !== day)
                                          .map(d => (
                                            <option key={d} value={d}>Día {d}</option>
                                          ))
                                        }
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Botón para agregar más videos */}
                            <Button
                              onClick={() => openUploadModal(day)}
                              variant="outline"
                              className="w-full border-green-500/50 text-green-400 hover:bg-green-500/20 rounded-xl"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Agregar Video
                            </Button>
                          </div>
                        ) : (
                          <Button
                            onClick={() => openUploadModal(day)}
                            className="w-full bg-pink-600 hover:bg-pink-500 text-white rounded-xl"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Subir Videos
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SECCIONES: Ebooks, Challenges, Blog, Recetas, Mindset */}
            {activeTab !== 'rutinas' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">
                    Contenido de {currentTabConfig?.label}
                  </h3>
                  <Button 
                    onClick={() => openUploadModal()}
                    className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Subir {activeTab === 'ebooks' ? 'Ebook' : 
                           activeTab === 'challenges' ? 'Challenge' :
                           activeTab === 'blog' ? 'Artículo' :
                           activeTab === 'recetas' ? 'Receta' : 'Contenido'}
                  </Button>
                </div>

                {/* Lista de contenido */}
                {contents.length === 0 ? (
                  <div className="bg-zinc-900 rounded-3xl p-12 text-center border border-zinc-800">
                    <Upload className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2 text-white">No hay contenido</h3>
                    <p className="text-zinc-500 mb-6">Sube tu primer {activeTab === 'ebooks' ? 'ebook' : 'contenido'} para esta sección</p>
                    <Button 
                      onClick={() => openUploadModal()}
                      className="bg-pink-600 hover:bg-pink-500 text-white rounded-full"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Subir Contenido
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contents.map((content) => (
                      <div key={content.content_id} className="bg-zinc-900 rounded-2xl p-4 border border-zinc-800 hover:border-pink-500/50 transition-all">
                        {/* Thumbnail - Mostrar cover_image si existe, sino file_path si es imagen, sino ícono */}
                        <div className="aspect-video bg-zinc-800 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
                          {content.cover_image ? (
                            <img 
                              src={content.cover_image.startsWith('http') ? content.cover_image : `${BACKEND_URL}${content.cover_image}`} 
                              alt={content.title}
                              className="w-full h-full object-cover"
                            />
                          ) : content.content_type === 'image' ? (
                            <img 
                              src={content.file_path.startsWith('http') ? content.file_path : `${BACKEND_URL}${content.file_path}`} 
                              alt={content.title}
                              className="w-full h-full object-cover"
                            />
                          ) : content.content_type === 'video' ? (
                            <Video className="w-12 h-12 text-pink-500" />
                          ) : (
                            <FileText className="w-12 h-12 text-red-400" />
                          )}
                        </div>

                        {/* Info */}
                        <h4 className="font-bold truncate mb-1 text-white">{content.title}</h4>
                        <p className="text-sm text-zinc-500 truncate mb-3">
                          {content.description || 'Sin descripción'}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-4">
                          <span>{formatFileSize(content.file_size)}</span>
                          <span>•</span>
                          <span>{new Date(content.created_at).toLocaleDateString('es-ES')}</span>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <a 
                            href={`${BACKEND_URL}${content.file_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-zinc-800 text-zinc-300 text-center py-2 rounded-lg text-sm font-medium hover:bg-zinc-700 transition-colors"
                          >
                            Ver
                          </a>
                          <button
                            onClick={() => setEditingContent(content)}
                            className="bg-blue-500/20 text-blue-400 p-2 rounded-lg hover:bg-blue-500/30 transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(content.content_id)}
                            className="bg-red-500/20 text-red-400 p-2 rounded-lg hover:bg-red-500/30 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL DE UPLOAD */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 sticky top-0 bg-zinc-900">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {activeTab === 'rutinas' && selectedDay 
                      ? `Subir Video - Día ${selectedDay}` 
                      : `Subir ${currentTabConfig?.label}`}
                  </h2>
                  {activeTab === 'rutinas' && (
                    <p className="text-sm text-zinc-400">{currentLevel?.name}</p>
                  )}
                </div>
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Selector de fuente de video (solo para videos) */}
              {(activeTab === 'rutinas' || activeTab === 'academia' || activeTab === 'recetas' || activeTab === 'mindset') && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">📹 Fuente del Video</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, video_source: 'upload', external_url: '' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.video_source === 'upload'
                          ? 'bg-pink-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <Upload className="w-5 h-5" />
                      <span className="text-xs">Subir Archivo</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, video_source: 'youtube', file: null, files: [] })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.video_source === 'youtube'
                          ? 'bg-red-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <Play className="w-5 h-5" />
                      <span className="text-xs">YouTube</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, video_source: 'drive', file: null, files: [] })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.video_source === 'drive'
                          ? 'bg-blue-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <FileText className="w-5 h-5" />
                      <span className="text-xs">Google Drive</span>
                    </button>
                  </div>
                </div>
              )}

              {/* URL externa (YouTube o Drive) */}
              {(uploadForm.video_source === 'youtube' || uploadForm.video_source === 'drive') && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">
                    {uploadForm.video_source === 'youtube' ? '🔗 URL de YouTube' : '🔗 URL de Google Drive'}
                  </label>
                  <Input
                    value={uploadForm.external_url}
                    onChange={(e) => setUploadForm({ ...uploadForm, external_url: e.target.value })}
                    placeholder={uploadForm.video_source === 'youtube' 
                      ? 'https://www.youtube.com/watch?v=... o https://youtu.be/...'
                      : 'https://drive.google.com/file/d/.../view'}
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    {uploadForm.video_source === 'youtube' 
                      ? 'Pega el enlace completo del video de YouTube'
                      : 'Asegúrate de que el archivo de Drive tenga permisos de visualización pública o por enlace'}
                  </p>
                </div>
              )}

              {/* Selector de fuente para Ebooks */}
              {activeTab === 'ebooks' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Fuente del Ebook</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, video_source: 'upload', external_url: '' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                        uploadForm.video_source === 'upload'
                          ? 'bg-blue-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      Subir Archivo
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, video_source: 'drive', file: null })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                        uploadForm.video_source === 'drive'
                          ? 'bg-blue-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      Enlace Drive
                    </button>
                  </div>
                </div>
              )}

              {/* URL de Google Drive para Ebooks */}
              {activeTab === 'ebooks' && uploadForm.video_source === 'drive' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2 text-zinc-300">URL de Google Drive *</label>
                  <input
                    type="url"
                    value={uploadForm.external_url}
                    onChange={(e) => setUploadForm({ ...uploadForm, external_url: e.target.value })}
                    placeholder="https://drive.google.com/file/d/.../view"
                    className="w-full px-4 py-3 border-2 border-zinc-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    💡 Asegúrate de que el archivo tenga permisos "Cualquier persona con el enlace puede ver"
                  </p>
                </div>
              )}

              {/* File Drop Zone - Solo si es upload de archivo */}
              {(uploadForm.video_source === 'upload' || (activeTab !== 'ebooks' && activeTab !== 'rutinas' && activeTab !== 'recetas' && activeTab !== 'academia')) && (activeTab !== 'ebooks' || uploadForm.video_source === 'upload') && (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
                  uploadForm.file || uploadForm.files.length > 0 ? 'border-green-300 bg-green-50' : 'border-zinc-200 hover:border-pink-300 hover:bg-pink-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={activeTab === 'rutinas' || activeTab === 'recetas' ? 'video/*' : 
                          activeTab === 'ebooks' ? 'application/pdf' : 
                          'video/*,application/pdf,image/*'}
                  onChange={activeTab === 'rutinas' && selectedDay ? handleMultipleFilesSelect : handleFileSelect}
                  multiple={false}
                  className="hidden"
                />
                {uploadForm.files.length > 0 ? (
                  <div className="space-y-2">
                    <Check className="w-8 h-8 text-green-600 mx-auto" />
                    <p className="font-medium text-green-700">{uploadForm.files.length} videos seleccionados</p>
                    <div className="max-h-24 overflow-y-auto">
                      {uploadForm.files.map((f, i) => (
                        <p key={i} className="text-xs text-green-600">{f.name} ({formatFileSize(f.size)})</p>
                      ))}
                    </div>
                  </div>
                ) : uploadForm.file ? (
                  <div className="flex items-center justify-center gap-3">
                    <Check className="w-8 h-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-green-700">{uploadForm.file.name}</p>
                      <p className="text-sm text-green-600">{formatFileSize(uploadForm.file.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
                    <p className="font-medium text-zinc-300">
                      {activeTab === 'rutinas' && selectedDay 
                        ? 'Haz clic para seleccionar uno o varios videos'
                        : 'Haz clic para seleccionar un archivo'}
                    </p>
                    <p className="text-sm text-zinc-500 mt-1">
                      {activeTab === 'rutinas' ? 'Videos hasta 10GB (puedes seleccionar múltiples)' : 
                       activeTab === 'ebooks' ? 'PDFs hasta 50MB' : 
                       'Videos (10GB), PDFs (50MB), Imágenes (10MB)'}
                    </p>
                  </>
                )}
              </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Título *</label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Título del contenido"
                  className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>

              {/* Campos adicionales para Blog */}
              {activeTab === 'blog' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">Autor *</label>
                    <Input
                      value={uploadForm.author}
                      onChange={(e) => setUploadForm({ ...uploadForm, author: e.target.value })}
                      placeholder="Nombre del autor"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">📂 Categoría del Artículo</label>
                    <Input
                      value={uploadForm.blog_category || ''}
                      onChange={(e) => setUploadForm({ ...uploadForm, blog_category: e.target.value })}
                      placeholder="Ej: Nutrición, Fitness..."
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                </div>
              )}

              {/* Campos adicionales para Podcasts/Mindset */}
              {activeTab === 'mindset' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">Invitado</label>
                    <Input
                      value={uploadForm.guest}
                      onChange={(e) => setUploadForm({ ...uploadForm, guest: e.target.value })}
                      placeholder="Nombre del invitado"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">Duración</label>
                    <Input
                      value={uploadForm.duration}
                      onChange={(e) => setUploadForm({ ...uploadForm, duration: e.target.value })}
                      placeholder="Ej: 45:30"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                </div>
              )}

              {/* Campos adicionales para Challenges */}
              {activeTab === 'challenges' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">💰 Precio ($) *</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={uploadForm.price}
                        onChange={(e) => setUploadForm({ ...uploadForm, price: e.target.value })}
                        placeholder="29.99"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">👥 Cupos Máximos</label>
                      <Input
                        type="number"
                        value={uploadForm.max_participants}
                        onChange={(e) => setUploadForm({ ...uploadForm, max_participants: e.target.value })}
                        placeholder="Ilimitado si está vacío"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">📅 Fecha de Inicio</label>
                      <Input
                        type="date"
                        value={uploadForm.start_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, start_date: e.target.value })}
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">📅 Fecha de Fin</label>
                      <Input
                        type="date"
                        value={uploadForm.end_date}
                        onChange={(e) => setUploadForm({ ...uploadForm, end_date: e.target.value })}
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">🏷️ Código de Descuento</label>
                      <Input
                        value={uploadForm.discount_code}
                        onChange={(e) => setUploadForm({ ...uploadForm, discount_code: e.target.value.toUpperCase() })}
                        placeholder="Ej: PROMO20"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">% Descuento</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={uploadForm.discount_percentage}
                        onChange={(e) => setUploadForm({ ...uploadForm, discount_percentage: e.target.value })}
                        placeholder="Ej: 20"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  
                  {/* Link de Telegram */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">📱 Link de Telegram</label>
                    <Input
                      value={uploadForm.telegram_link || ''}
                      onChange={(e) => setUploadForm({ ...uploadForm, telegram_link: e.target.value })}
                      placeholder="https://t.me/tu_grupo"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Los participantes tendrán acceso a este link al inscribirse
                    </p>
                  </div>
                  
                  {/* Beneficios del Challenge */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">✨ Beneficios / Qué Incluye</label>
                    <textarea
                      value={uploadForm.challenge_benefits || ''}
                      onChange={(e) => setUploadForm({ ...uploadForm, challenge_benefits: e.target.value })}
                      placeholder="• Videos de entrenamiento diarios&#10;• Plan de nutrición&#10;• Acceso al grupo de Telegram&#10;• Soporte personalizado"
                      rows={4}
                      className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder:text-zinc-500 text-sm"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Puedes agregar materiales adicionales después de crear el challenge
                    </p>
                  </div>
                </>
              )}

              {/* Campos específicos para Academia */}
              {activeTab === 'academia' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">📚 Categoría del Contenido *</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, academia_category: 'entrenamiento' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.academia_category === 'entrenamiento'
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">💪</span>
                      <span className="text-sm">Entrenamiento</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, academia_category: 'nutricion' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.academia_category === 'nutricion'
                          ? 'bg-green-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">🥗</span>
                      <span className="text-sm">Nutrición</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadForm({ ...uploadForm, academia_category: 'suplementacion' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        uploadForm.academia_category === 'suplementacion'
                          ? 'bg-purple-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">💊</span>
                      <span className="text-sm">Suplementación</span>
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">
                    Selecciona la categoría para organizar el contenido en la Academia
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  {activeTab === 'blog' ? 'Contenido del Artículo *' : 'Descripción'}
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder={activeTab === 'blog' ? 'Escribe el contenido completo del artículo...' : 'Descripción opcional...'}
                  rows={activeTab === 'blog' ? 10 : 3}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder:text-zinc-500"
                />
              </div>

              {/* Imagen de Portada - Para TODOS los tipos de contenido */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">🖼️ Imagen de Portada {activeTab === 'blog' ? '' : '(Opcional)'}</label>
                <div 
                  onClick={() => coverInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    uploadForm.coverImage ? 'border-blue-300 bg-blue-50' : 'border-zinc-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageSelect}
                    className="hidden"
                  />
                  {uploadForm.coverImage ? (
                    <div className="flex items-center justify-center gap-3">
                      <Image className="w-6 h-6 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium text-blue-700 text-sm">{uploadForm.coverImage.name}</p>
                        <p className="text-xs text-blue-600">{formatFileSize(uploadForm.coverImage.size)}</p>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setUploadForm({...uploadForm, coverImage: null}); }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <Image className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">Haz clic para agregar imagen de portada</p>
                      <p className="text-xs text-zinc-400 mt-1">JPG, PNG o WebP (máx 5MB)</p>
                    </>
                  )}
                </div>
              </div>

              {/* Level selector MÚLTIPLE - REMOVIDO: ahora se gestiona todo desde "Programas Vinculados" */}

              {/* Programas Vinculados (opcional) - UPLOAD */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  Programas Vinculados <span className="text-zinc-500 font-normal text-xs">(opcional — deja vacío para usar reglas de nivel)</span>
                </label>
                {programs.length === 0 ? (
                  <p className="text-xs text-zinc-500">No hay programas configurados.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-zinc-700 rounded-xl bg-zinc-800/50">
                    {programs.map(prog => {
                      const isSel = uploadForm.program_ids?.includes(prog.program_id);
                      return (
                        <label
                          key={prog.program_id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                            isSel
                              ? 'bg-pink-500/20 border border-pink-500/50 text-white'
                              : 'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-pink-500/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSel}
                            onChange={() => toggleProgramForForm(uploadForm, setUploadForm, prog.program_id)}
                            className="w-4 h-4 accent-pink-600"
                            data-testid={`cm-upload-program-${prog.program_id}`}
                          />
                          <span className="truncate">{prog.name || prog.title || prog.program_id}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Opciones de acceso removidas — todo se controla ahora desde "Programas Vinculados" */}

              {/* Price (si es de pago) */}
              {uploadForm.access_options.includes('paid') && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Precio ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={uploadForm.price}
                    onChange={(e) => setUploadForm({ ...uploadForm, price: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                </div>
              )}

              {/* Código de Descuento */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">🏷️ Código de Descuento (Opcional)</label>
                <Input
                  value={uploadForm.discount_code}
                  onChange={(e) => setUploadForm({ ...uploadForm, discount_code: e.target.value.toUpperCase() })}
                  placeholder="Ej: DESCUENTO20, PROMO50"
                  className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Código que los usuarios pueden usar para obtener descuento en este contenido
                </p>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-pink-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-pink-700">Subiendo...</span>
                    <span className="text-sm font-bold text-pink-700">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-pink-200 rounded-full h-2">
                    <div 
                      className="bg-pink-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button
                onClick={handleUpload}
                disabled={
                  uploading || 
                  !uploadForm.title ||
                  (
                    // Si es video externo, necesita URL
                    (uploadForm.video_source === 'youtube' || uploadForm.video_source === 'drive') 
                      ? !uploadForm.external_url
                      // Si es upload normal
                      : (activeTab === 'rutinas' && selectedDay) 
                        ? (uploadForm.files.length === 0 && !uploadForm.file)
                        : (activeTab !== 'blog' && !uploadForm.file)
                  )
                }
                className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold disabled:opacity-50"
              >
                {uploading ? 'Subiendo...' : 'Subir Contenido'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDICIÓN */}
      {editingContent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 sticky top-0 bg-zinc-900">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Editar Contenido</h2>
                <button 
                  onClick={() => setEditingContent(null)}
                  className="p-2 hover:bg-zinc-800 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Archivo actual */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="block text-sm font-medium mb-2 text-zinc-300">📁 Archivo Actual</label>
                <div className="flex items-center gap-3">
                  {editingContent.content_type === 'video' && <Video className="w-6 h-6 text-pink-600" />}
                  {editingContent.content_type === 'pdf' && <FileText className="w-6 h-6 text-red-600" />}
                  {editingContent.content_type === 'image' && <Image className="w-6 h-6 text-blue-600" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium truncate">{editingContent.file_name || 'Archivo'}</p>
                    <p className="text-xs text-zinc-500">{formatFileSize(editingContent.file_size)}</p>
                  </div>
                  <a 
                    href={`${BACKEND_URL}${editingContent.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-600 text-sm hover:underline"
                  >
                    Ver
                  </a>
                </div>
              </div>

              {/* Reemplazar archivo */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">🔄 Reemplazar Archivo (Opcional)</label>
                <div 
                  onClick={() => document.getElementById('edit-file-input')?.click()}
                  className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    editingContent.newFile ? 'border-green-300 bg-green-50' : 'border-zinc-200 hover:border-pink-300 hover:bg-pink-50'
                  }`}
                >
                  <input
                    id="edit-file-input"
                    type="file"
                    accept="video/*,application/pdf,image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setEditingContent({ ...editingContent, newFile: file });
                      }
                    }}
                    className="hidden"
                  />
                  {editingContent.newFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <Check className="w-6 h-6 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-green-700 text-sm">{editingContent.newFile.name}</p>
                        <p className="text-xs text-green-600">{formatFileSize(editingContent.newFile.size)}</p>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setEditingContent({...editingContent, newFile: null}); }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">Clic para seleccionar nuevo archivo</p>
                      <p className="text-xs text-zinc-400 mt-1">Dejalo vacío para mantener el actual</p>
                    </>
                  )}
                </div>
              </div>

              {/* Imagen de Portada en Edición */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">🖼️ Imagen de Portada</label>
                {editingContent.cover_image && !editingContent.newCoverImage && (
                  <div className="mb-2 flex items-center gap-2 bg-blue-50 rounded-lg p-2">
                    <img 
                      src={editingContent.cover_image.startsWith('http') ? editingContent.cover_image : `${BACKEND_URL}${editingContent.cover_image}`} 
                      alt="Portada actual"
                      className="w-16 h-16 object-cover rounded"
                    />
                    <span className="text-xs text-blue-700">Portada actual</span>
                  </div>
                )}
                <div 
                  onClick={() => document.getElementById('edit-cover-input')?.click()}
                  className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    editingContent.newCoverImage ? 'border-blue-300 bg-blue-50' : 'border-zinc-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <input
                    id="edit-cover-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setEditingContent({ ...editingContent, newCoverImage: file });
                      }
                    }}
                    className="hidden"
                  />
                  {editingContent.newCoverImage ? (
                    <div className="flex items-center justify-center gap-3">
                      <Image className="w-6 h-6 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium text-blue-700 text-sm">{editingContent.newCoverImage.name}</p>
                        <p className="text-xs text-blue-600">{formatFileSize(editingContent.newCoverImage.size)}</p>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setEditingContent({...editingContent, newCoverImage: null}); }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <Image className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">{editingContent.cover_image ? 'Cambiar portada' : 'Agregar portada'}</p>
                      <p className="text-xs text-zinc-400 mt-1">JPG, PNG o WebP (máx 5MB)</p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Título</label>
                <Input
                  value={editingContent.title}
                  onChange={(e) => setEditingContent({ ...editingContent, title: e.target.value })}
                  className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Descripción</label>
                <textarea
                  value={editingContent.description || ''}
                  onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })}
                  rows={editingContent.category === 'blog' ? 8 : 3}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder:text-zinc-500"
                />
              </div>

              {/* === CAMPOS ESPECÍFICOS PARA EBOOKS === */}
              {editingContent.category === 'ebook' && (
                <div className="bg-blue-50 rounded-xl p-4 space-y-4">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <FileText className="w-5 h-5" />
                    <span>Configuración del Ebook</span>
                  </div>
                  
                  {/* Selector de fuente */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">Fuente del Ebook</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingContent({ ...editingContent, video_source: 'upload' })}
                        className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                          editingContent.video_source !== 'drive'
                            ? 'bg-blue-500 text-white'
                            : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 border border-zinc-200'
                        }`}
                      >
                        📁 Archivo Subido
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingContent({ ...editingContent, video_source: 'drive' })}
                        className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                          editingContent.video_source === 'drive'
                            ? 'bg-blue-500 text-white'
                            : 'bg-zinc-900 text-zinc-400 hover:bg-zinc-800 border border-zinc-200'
                        }`}
                      >
                        🔗 Enlace Drive
                      </button>
                    </div>
                  </div>

                  {/* URL de Drive */}
                  {editingContent.video_source === 'drive' && (
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">URL de Google Drive *</label>
                      <Input
                        type="url"
                        value={editingContent.external_url || editingContent.file_path || ''}
                        onChange={(e) => setEditingContent({ 
                          ...editingContent, 
                          external_url: e.target.value,
                          file_path: e.target.value 
                        })}
                        placeholder="https://drive.google.com/file/d/.../view"
                        className="rounded-xl bg-zinc-900"
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        💡 Asegúrate de que el archivo tenga permisos "Cualquier persona con el enlace"
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* === CAMPOS ESPECÍFICOS PARA CHALLENGES === */}
              {editingContent.category === 'challenge' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">💰 Precio ($)</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingContent.price || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, price: e.target.value })}
                        placeholder="29.99"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">👥 Cupos Máximos</label>
                      <Input
                        type="number"
                        value={editingContent.max_participants || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, max_participants: e.target.value })}
                        placeholder="Ilimitado"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">📅 Fecha de Inicio</label>
                      <Input
                        type="date"
                        value={editingContent.start_date || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, start_date: e.target.value })}
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">📅 Fecha de Fin</label>
                      <Input
                        type="date"
                        value={editingContent.end_date || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, end_date: e.target.value })}
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">🏷️ Código de Descuento</label>
                      <Input
                        value={editingContent.discount_code || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, discount_code: e.target.value.toUpperCase() })}
                        placeholder="Ej: PROMO20"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-zinc-300">% Descuento</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={editingContent.discount_percentage || ''}
                        onChange={(e) => setEditingContent({ ...editingContent, discount_percentage: e.target.value })}
                        placeholder="20"
                        className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                      />
                    </div>
                  </div>
                  
                  {/* Link de Telegram */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">📱 Link de Telegram</label>
                    <Input
                      value={editingContent.telegram_link || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, telegram_link: e.target.value })}
                      placeholder="https://t.me/..."
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Los participantes tendrán acceso a este link al inscribirse
                    </p>
                  </div>
                  
                  {/* Beneficios del Challenge */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">✨ Beneficios / Qué Incluye</label>
                    <textarea
                      value={editingContent.challenge_benefits || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, challenge_benefits: e.target.value })}
                      placeholder="• Videos de entrenamiento diarios&#10;• Plan de nutrición&#10;• Acceso al grupo de Telegram&#10;• Soporte personalizado"
                      rows={4}
                      className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder:text-zinc-500 text-sm"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Usa una lista con viñetas (•) para mejor presentación
                    </p>
                  </div>
                  
                  {/* Materiales Adicionales */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                    <label className="block text-sm font-bold mb-3 text-amber-800">📦 Materiales del Challenge</label>
                    
                    {/* Lista de materiales existentes */}
                    {editingContent.challenge_materials && editingContent.challenge_materials.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {editingContent.challenge_materials.map((mat, idx) => (
                          <div key={mat.material_id || idx} className="flex items-center gap-2 bg-zinc-900 rounded-lg p-2">
                            <span className="text-lg">
                              {mat.type === 'pdf' ? '📄' : mat.type === 'video' ? '🎬' : '🖼️'}
                            </span>
                            <span className="flex-1 text-sm font-medium truncate">{mat.title}</span>
                            <a 
                              href={`${BACKEND_URL}${mat.file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 text-xs hover:underline"
                            >
                              Ver
                            </a>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm('¿Eliminar este material?')) return;
                                try {
                                  await axios.delete(
                                    `${BACKEND_URL}/api/content/${editingContent.content_id}/materials/${mat.material_id}`,
                                    { headers: getHeaders() }
                                  );
                                  // Actualizar estado local
                                  setEditingContent({
                                    ...editingContent,
                                    challenge_materials: editingContent.challenge_materials.filter(m => m.material_id !== mat.material_id)
                                  });
                                  toast.success('Material eliminado');
                                } catch (error) {
                                  toast.error('Error al eliminar material');
                                }
                              }}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Formulario para agregar nuevo material */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="Título del material"
                          id="new-material-title"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <input
                          type="file"
                          id="new-material-file"
                          accept="video/*,application/pdf,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            const title = document.getElementById('new-material-title')?.value;
                            if (!file || !title) {
                              toast.error('Ingresa un título y selecciona un archivo');
                              return;
                            }
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            formData.append('title', title);
                            
                            try {
                              toast.info('Subiendo material...');
                              const response = await axios.post(
                                `${BACKEND_URL}/api/content/${editingContent.content_id}/materials`,
                                formData,
                                { headers: { Authorization: getHeaders().Authorization } }
                              );
                              
                              // Actualizar estado local
                              setEditingContent({
                                ...editingContent,
                                challenge_materials: [
                                  ...(editingContent.challenge_materials || []),
                                  response.data.material
                                ]
                              });
                              
                              // Limpiar inputs
                              document.getElementById('new-material-title').value = '';
                              e.target.value = '';
                              
                              toast.success('Material agregado');
                            } catch (error) {
                              toast.error('Error al subir material');
                            }
                          }}
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById('new-material-file')?.click()}
                          variant="outline"
                          className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      Agrega ebooks, videos de apoyo o PDFs que recibirán los participantes
                    </p>
                  </div>
                </>
              )}

              {/* === CAMPOS ESPECÍFICOS PARA BLOG === */}
              {editingContent.category === 'blog' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">✍️ Autor</label>
                    <Input
                      value={editingContent.author || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, author: e.target.value })}
                      placeholder="Nombre del autor"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">📂 Categoría del Artículo</label>
                    <Input
                      value={editingContent.blog_category || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, blog_category: e.target.value })}
                      placeholder="Ej: Nutrición, Fitness..."
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                </div>
              )}

              {/* === CAMPOS ESPECÍFICOS PARA PODCASTS/MINDSET === */}
              {editingContent.category === 'mindset' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">🎤 Invitado</label>
                    <Input
                      value={editingContent.guest || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, guest: e.target.value })}
                      placeholder="Nombre del invitado"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-zinc-300">⏱️ Duración</label>
                    <Input
                      value={editingContent.duration || ''}
                      onChange={(e) => setEditingContent({ ...editingContent, duration: e.target.value })}
                      placeholder="Ej: 45:30"
                      className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                    />
                  </div>
                </div>
              )}

              {/* === CAMPOS ESPECÍFICOS PARA ACADEMIA === */}
              {editingContent.category === 'academia' && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">📚 Categoría del Contenido</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingContent({ ...editingContent, academia_category: 'entrenamiento' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        editingContent.academia_category === 'entrenamiento'
                          ? 'bg-orange-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">💪</span>
                      <span className="text-sm">Entrenamiento</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingContent({ ...editingContent, academia_category: 'nutricion' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        editingContent.academia_category === 'nutricion'
                          ? 'bg-green-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">🥗</span>
                      <span className="text-sm">Nutrición</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingContent({ ...editingContent, academia_category: 'suplementacion' })}
                      className={`py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                        editingContent.academia_category === 'suplementacion'
                          ? 'bg-purple-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      <span className="text-xl">💊</span>
                      <span className="text-sm">Suplementación</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Niveles múltiples en edición - REMOVIDO: se gestiona todo desde Programas Vinculados */}

              {/* Programas Vinculados (opcional) - EDIT */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  Programas Vinculados <span className="text-zinc-500 font-normal text-xs">(opcional — si no cambias nada, el acceso actual se mantiene)</span>
                </label>
                <p className="text-xs text-zinc-500 mb-2" data-testid="cm-edit-current-access">
                  Acceso actual:{' '}
                  {(editingContent.program_ids || []).length > 0
                    ? (editingContent.program_ids || []).map(pid => (programs.find(p => p.program_id === pid)?.name || pid).trim()).join(' + ')
                    : `por nivel (${(editingContent.levels || [editingContent.level || 'free']).join(', ')})`}
                  {editingContent.active === false ? ' · inactivo (borrador)' : ''}
                </p>
                {programs.length === 0 ? (
                  <p className="text-xs text-zinc-500">No hay programas configurados.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-zinc-700 rounded-xl bg-zinc-800/50">
                    {programs.map(prog => {
                      const isSel = editingContent.program_ids?.includes(prog.program_id);
                      return (
                        <label
                          key={prog.program_id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm ${
                            isSel
                              ? 'bg-pink-500/20 border border-pink-500/50 text-white'
                              : 'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:border-pink-500/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={!!isSel}
                            onChange={() => toggleProgramForForm(editingContent, setEditingContent, prog.program_id)}
                            className="w-4 h-4 accent-pink-600"
                            data-testid={`cm-edit-program-${prog.program_id}`}
                          />
                          <span className="truncate">{prog.name || prog.title || prog.program_id}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Opciones de acceso en edición — removidas: se gestiona todo desde Programas Vinculados */}

              {/* Precio en edición */}
              {(editingContent.access_options || []).includes('paid') && (
                <div>
                  <label className="block text-sm font-medium mb-2 text-zinc-300">Precio ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingContent.price || ''}
                    onChange={(e) => setEditingContent({ ...editingContent, price: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                  />
                </div>
              )}

              <Button
                onClick={async () => {
                  try {
                    let newFilePath = null;
                    let newCoverPath = null;

                    // Si hay nuevo archivo, subirlo primero
                    if (editingContent.newFile) {
                      const formData = new FormData();
                      formData.append('file', editingContent.newFile);
                      formData.append('title', editingContent.title);
                      formData.append('description', editingContent.description || '');
                      formData.append('category', editingContent.category);
                      formData.append('levels', (editingContent.levels || [editingContent.level]).join(','));
                      formData.append('access_options', (editingContent.access_options || ['free']).join(','));
                      if (editingContent.price) formData.append('price', editingContent.price);
                      if (editingContent.discount_code) formData.append('discount_code', editingContent.discount_code);
                      if (editingContent.discount_percentage) formData.append('discount_percentage', editingContent.discount_percentage);
                      if (editingContent.newCoverImage) formData.append('cover_image', editingContent.newCoverImage);
                      if (editingContent.program_ids && editingContent.program_ids.length > 0) {
                        formData.append('program_ids', editingContent.program_ids.join(','));
                      }
                      // Campos Challenge
                      if (editingContent.start_date) formData.append('start_date', editingContent.start_date);
                      if (editingContent.end_date) formData.append('end_date', editingContent.end_date);
                      if (editingContent.max_participants) formData.append('max_participants', editingContent.max_participants);
                      // Campos Blog
                      if (editingContent.author) formData.append('author', editingContent.author);
                      if (editingContent.blog_category) formData.append('blog_category', editingContent.blog_category);
                      // Campos Podcast
                      if (editingContent.guest) formData.append('guest', editingContent.guest);
                      if (editingContent.duration) formData.append('duration', editingContent.duration);

                      const uploadResponse = await axios.post(`${BACKEND_URL}/api/content/upload`, formData, {
                        headers: { Authorization: getHeaders().Authorization }
                      });
                      newFilePath = uploadResponse.data.file_path;
                      newCoverPath = uploadResponse.data.cover_image;

                      // Eliminar el contenido anterior
                      await axios.delete(`${BACKEND_URL}/api/content/${editingContent.content_id}`, { headers: getHeaders() });
                      
                      toast.success('Contenido reemplazado exitosamente');
                      setEditingContent(null);
                      fetchContents();
                      return;
                    }

                    // Si solo hay nueva imagen de portada
                    if (editingContent.newCoverImage) {
                      const coverFormData = new FormData();
                      coverFormData.append('image', editingContent.newCoverImage);
                      
                      const coverResponse = await axios.post(`${BACKEND_URL}/api/content/upload-cover`, coverFormData, {
                        headers: { Authorization: getHeaders().Authorization }
                      });
                      newCoverPath = coverResponse.data.url;
                    }

                    // Actualizar metadatos - usa Programas Vinculados como fuente de acceso
                    // El acceso solo se modifica si se tocó "Programas Vinculados"; si no, se conserva el actual.
                    // (Antes, guardar cualquier edición reiniciaba el acceso a 'free' y dejaba el contenido inactivo.)
                    let accessFields = {};
                    if (editingContent.programsTouched) {
                      const selectedPrograms = (editingContent.program_ids || []).filter(Boolean);
                      if (selectedPrograms.length > 0) {
                        const resolved = resolveAccessPayload(selectedPrograms);
                        accessFields = {
                          levels: resolved.levels,
                          access_options: resolved.access_options,
                          program_ids: resolved.program_ids,
                          active: resolved.active,
                        };
                      } else {
                        // Se quitaron todos los programas: sin restricción por programa, el resto del acceso no cambia
                        accessFields = { program_ids: [] };
                      }
                    }
                    const updateData = {
                      title: editingContent.title,
                      description: editingContent.description,
                      ...accessFields,
                      price: editingContent.price,
                      discount_code: editingContent.discount_code,
                      discount_percentage: editingContent.discount_percentage,
                      // Campos Challenge
                      start_date: editingContent.start_date,
                      end_date: editingContent.end_date,
                      max_participants: editingContent.max_participants,
                      // Campos Blog
                      author: editingContent.author,
                      blog_category: editingContent.blog_category,
                      // Campos Podcast
                      guest: editingContent.guest,
                      duration: editingContent.duration,
                    };

                    if (newCoverPath) {
                      updateData.cover_image = newCoverPath;
                    }

                    await axios.put(`${BACKEND_URL}/api/content/${editingContent.content_id}`, updateData, { headers: getHeaders() });
                    toast.success('Contenido actualizado');
                    setEditingContent(null);
                    fetchContents();
                  } catch (error) {
                    console.error('Error updating content:', error);
                    toast.error('Error al actualizar');
                  }
                }}
                className="w-full bg-pink-600 hover:bg-pink-700 text-white rounded-full py-6 font-bold"
              >
                Guardar Cambios
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDICIÓN DE VIDEO */}
      {editingVideo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-3xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">Editar Video</h2>
                  <p className="text-sm text-zinc-500">Día {editingVideo.day} - Video {editingVideo.videoIndex + 1}</p>
                </div>
                <button 
                  onClick={() => setEditingVideo(null)}
                  className="p-2 hover:bg-zinc-800 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Título del video */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Título del Video</label>
                <Input
                  value={editVideoForm.title}
                  onChange={(e) => setEditVideoForm({ ...editVideoForm, title: e.target.value })}
                  placeholder="Ej: Sentadillas con barra"
                  className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>

              {/* Descripción del video */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Descripción</label>
                <textarea
                  value={editVideoForm.description}
                  onChange={(e) => setEditVideoForm({ ...editVideoForm, description: e.target.value })}
                  placeholder="Describe el ejercicio, técnica, músculos trabajados, etc."
                  rows={3}
                  className="w-full px-4 py-2 border border-zinc-700 rounded-xl resize-none bg-zinc-800 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              {/* Fuente del video */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">Fuente del Video</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditVideoForm({ ...editVideoForm, video_source: 'upload' })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      editVideoForm.video_source === 'upload'
                        ? 'bg-pink-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Archivo
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditVideoForm({ ...editVideoForm, video_source: 'youtube' })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      editVideoForm.video_source === 'youtube'
                        ? 'bg-red-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    YouTube
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditVideoForm({ ...editVideoForm, video_source: 'drive' })}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      editVideoForm.video_source === 'drive'
                        ? 'bg-blue-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    Drive
                  </button>
                </div>
              </div>

              {/* URL del video */}
              <div>
                <label className="block text-sm font-medium mb-2 text-zinc-300">
                  {editVideoForm.video_source === 'upload' ? 'Ruta del archivo' : 'URL del video'}
                </label>
                <Input
                  value={editVideoForm.video_url}
                  onChange={(e) => setEditVideoForm({ ...editVideoForm, video_url: e.target.value })}
                  placeholder={
                    editVideoForm.video_source === 'youtube' 
                      ? 'https://www.youtube.com/watch?v=...'
                      : editVideoForm.video_source === 'drive'
                      ? 'https://drive.google.com/file/d/.../view'
                      : '/content_files/videos/...'
                  }
                  className="rounded-xl bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                />
                {editVideoForm.video_source === 'youtube' && (
                  <p className="text-xs text-zinc-400 mt-1">Pega el enlace completo de YouTube</p>
                )}
                {editVideoForm.video_source === 'drive' && (
                  <p className="text-xs text-zinc-400 mt-1">Asegúrate de que el video tenga permisos "Cualquier persona con el enlace"</p>
                )}
              </div>

              {/* Botones de acción */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setEditingVideo(null)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={saveVideoEdit}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar Cambios
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default AdminContentManager;
